import React, { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { Layers, Menu, X } from "lucide-react";
import { Sidebar } from "./Sidebar";
import { SupportBanner } from "./SupportBanner";
import { PwaUpdatePrompt } from "./PwaUpdatePrompt";
import { useAuth } from "../../context/AuthContext";
import { OnboardingProvider } from "../../pages/onboarding/lib/OnboardingProvider";
import { SetupIncompleteBanner } from "../../pages/onboarding/components/SetupIncompleteBanner";
import { RoleFirstLoginTour } from "../onboarding/RoleFirstLoginTour";
import { TerminologyProvider } from "../../context/TerminologyContext";
import { CoachingAssignmentAlert } from "./CoachingAssignmentAlert";
import { AnnouncementPopup } from "./AnnouncementPopup";

export function AppShell() {
  const { user } = useAuth();
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    if (!navOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setNavOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [navOpen]);

  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-white text-slate-500 text-sm">
        Loading…
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (user.mfa_required_setup) return <Navigate to="/mfa-setup" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;

  return (
    <TerminologyProvider>
      <OnboardingProvider>
        <div className="min-h-screen bg-[#FDFDFD] text-[#111] flex flex-col">
          <SupportBanner />
          <SetupIncompleteBanner />
          <div className="flex flex-1 min-w-0">
            <button
              type="button"
              aria-label="Close navigation"
              className={`fixed inset-0 z-40 bg-slate-950/45 transition-opacity md:hidden ${navOpen ? "opacity-100" : "pointer-events-none opacity-0"}`}
              onClick={() => setNavOpen(false)}
            />
            <Sidebar open={navOpen} onNavigate={() => setNavOpen(false)} />
            <main className="flex-1 min-w-0 overflow-x-hidden">
              <div className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-slate-200 bg-white/95 px-3 backdrop-blur md:hidden">
                <button
                  type="button"
                  aria-label={navOpen ? "Close navigation" : "Open navigation"}
                  aria-controls="primary-navigation"
                  aria-expanded={navOpen}
                  className="grid h-11 w-11 place-items-center rounded-md border border-slate-200 text-slate-700"
                  onClick={() => setNavOpen((value) => !value)}
                >
                  {navOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <span className="grid h-8 w-8 place-items-center rounded bg-slate-900">
                    <Layers className="h-4 w-4 text-white" />
                  </span>
                  RemediKeep
                </div>
                <span className="h-11 w-11" aria-hidden="true" />
              </div>
              <Outlet />
            </main>
          </div>
          <RoleFirstLoginTour />
          <CoachingAssignmentAlert />
          <AnnouncementPopup />
          <PwaUpdatePrompt />
        </div>
      </OnboardingProvider>
    </TerminologyProvider>
  );
}
